import { useEffect, useRef, useState } from "react";
import { authApi } from "@/lib/api";

/**
 * Lightweight WS subscription that listens ONLY for `kpis_update` events from
 * `/api/ws/fleet`. Keeps the dashboard's top-bar metrics live without the old
 * 5-second HTTP poll. Falls back to a 30s safety poll if the WS drops.
 *
 * Backend emits `{type:"kpis_update", kpis:{...}}` from the geofence tick loop
 * every ~5s for every company that has at least one open WS connection.
 */
export function useKpis(initialKpis = {}) {
  const [kpis, setKpis] = useState(initialKpis);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const stoppedRef = useRef(false);
  const retryRef = useRef(null);

  useEffect(() => {
    stoppedRef.current = false;

    async function connect() {
      if (stoppedRef.current) return;
      let token;
      try {
        token = await authApi.wsToken();
      } catch (e) {
        console.warn("[useKpis] wsToken fetch failed; retrying in 5s", e);
        retryRef.current = setTimeout(connect, 5000);
        return;
      }
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = backend.replace(/^http/, "ws") + `/api/ws/fleet?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "kpis_update" && msg.kpis) {
              setKpis(msg.kpis);
            }
          } catch (err) {
            // Malformed WS payload — keep going, but surface in dev tools
            console.warn("[useKpis] failed to parse WS frame", err);
          }
        };
        ws.onclose = () => {
          setConnected(false);
          wsRef.current = null;
          if (!stoppedRef.current) {
            retryRef.current = setTimeout(connect, 2500);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch (_e) { /* WS already torn down */ } };
      } catch (err) {
        console.warn("[useKpis] WebSocket constructor threw", err);
        setConnected(false);
      }
    }

    connect();
    return () => {
      stoppedRef.current = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* WS already closed */ } }
    };
  }, []);

  return { kpis, setKpis, kpiWsConnected: connected };
}
